import EmptyState, { emptyStateTitleId } from 'components/EmptyState';
import { TaskAltOutlined } from 'components/icons';
import PartyName from 'components/party/PartyName';
import { Button } from 'components/ui/button';
import Unmasked from 'components/Unmasked';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import useAuth from 'hooks/useAuth';
import MyPendingInvoicesTab, { MY_PENDING_INVOICES_FILTER } from 'pages/dashboard/MyPendingInvoicesTab';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetInvoicesWithFiltersQuery } from 'store/api/invoices';
import { InvoiceStatusType } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import HomeSection, { HOME_LINK_CLASS } from './HomeSection';

const PREVIEW_SIZE = 3;
/** The empty section's title, as its empty state shows it and as the section's live region says it. */
const EMPTY_TITLE_ID = 'home.to-review-empty.title';

/** Why a pending invoice waits on the user, read from its status, and the step it waits for. */
const PENDING_STEP: Partial<Record<string, { reasonId: string; actionId: string }>> = {
    [InvoiceStatusType.DRAFT]: { reasonId: 'home.waiting-review', actionId: 'home.review' },
    [InvoiceStatusType.REVIEWED]: { reasonId: 'home.waiting-approval', actionId: 'home.approve' },
};

/** The `tab` value `InvoicePage` sends the user back with after a postpone; Home scrolls to this section. */
const PENDING_INVOICES_TAB = 'mypendinginvoices';

/**
 * The dashboard's My pending invoices: the first few, and the whole list in place on Show all, since the
 * Invoices list has no filter for them. While they load the card holds its title alone.
 */
export default function ToReviewSection() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [expanded, setExpanded] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    const { data, isError, refetch } = useGetInvoicesWithFiltersQuery(
        { filters: MY_PENDING_INVOICES_FILTER, page: 0, pageSize: PREVIEW_SIZE },
        { skip: !user?.id }
    );
    const count = data?.totalElements ?? 0;
    const emptyStatus = count === 0 ? t(EMPTY_TITLE_ID) : '';
    const scrollHere = count > 0 && searchParams.get('tab') === PENDING_INVOICES_TAB;

    useEffect(() => {
        if (scrollHere) {
            sectionRef.current?.scrollIntoView({ block: 'start' });
        }
    }, [scrollHere]);

    return (
        <HomeSection
            ref={sectionRef}
            className="gap-0.5 overflow-hidden"
            title={
                <>
                    {t('home.to-review-or-approve')}
                    {count > 0 ? (
                        <span className="text-muted-foreground font-medium tabular-nums"> · {count}</span>
                    ) : null}
                </>
            }
            actions={
                expanded || count === 0 ? null : (
                    <Button
                        variant="link"
                        className={cn(HOME_LINK_CLASS, 'h-auto p-0')}
                        onClick={() => setExpanded(true)}
                    >
                        {t('home.show-all')}
                    </Button>
                )
            }
        >
            {!data ? (
                isError ? (
                    <EmptyState size="panel" variant="error" onRetry={refetch} className="min-h-0 flex-1 py-4" />
                ) : null
            ) : count === 0 ? (
                <EmptyState
                    size="panel"
                    icon={TaskAltOutlined}
                    titleId={EMPTY_TITLE_ID}
                    bodyId="home.to-review-empty.body"
                    className="min-h-0 flex-1 py-4"
                />
            ) : expanded ? (
                // The dashboard's list, edge to edge in the card as it was in its own.
                <div className="-mx-5 min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
                    <MyPendingInvoicesTab />
                </div>
            ) : (
                <ul className="min-h-0 flex-1 overflow-y-auto">
                    {data.content.map((invoice) => {
                        const step = PENDING_STEP[invoice.invoiceStatusTypeCode];
                        return (
                            <li
                                key={invoice.id}
                                className="border-divider-soft flex min-h-12 items-center gap-2.5 border-t py-1.5"
                            >
                                <div className="flex min-w-0 flex-1 flex-col">
                                    <p className="flex min-w-0 items-center gap-1 font-medium whitespace-nowrap">
                                        <span className="text-meta font-mono">
                                            <Unmasked>{invoice.invoiceNumber}</Unmasked>
                                        </span>
                                        ·
                                        <PartyName party={invoice.clientParty} variant="readonly-other" singleLine />
                                    </p>
                                    {step ? (
                                        <p className="text-muted-foreground text-caption">{t(step.reasonId)}</p>
                                    ) : null}
                                </div>
                                {step ? (
                                    <LinkWithOrigin to={`/invoice/${invoice.id}`} className={HOME_LINK_CLASS}>
                                        {t(step.actionId)}
                                    </LinkWithOrigin>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}
            {/* What the load ended in, for assistive tech. There from the first render, before its words are. */}
            <div role="status" className="sr-only">
                {data ? emptyStatus : isError ? t(emptyStateTitleId('error')) : ''}
            </div>
        </HomeSection>
    );
}
